import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch,
    update,
} from '../services/SubscriptionItemsService';
import {SubscriptionItem} from '../types';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';
import {subscriptionItemsKeys} from './useSubscriptionItemsQuery';

/**
 * Single-record hook for the detail page and the add/edit drawers.
 *
 * FNP-918 changes:
 *
 * ⚠️ `createRecord` / `updateRecord` are `mutateAsync`, NOT `mutate`.
 * `mutate` returns void, so the drawer's `await handleSubmit(values)` resolved
 * on the same tick and `onClose()` ran before the server answered — a
 * server-side rejection then destroyed the operator's input and toasted into a
 * closed drawer.
 *
 * ⚠️ The mutations used to inject `customerId` into the payload
 * (`Object.assign({}, values, {customerId})`). No SubscriptionItem Input type
 * declares `customerId` — the scope field is `subscription`, and only on
 * create. Payload whitelisting now lives in the service.
 *
 * ⚠️ `fetch` takes the id alone: `getSubscriptionItem(id: ID!)`.
 */
export const useSubscriptionItem = (
    subscriptionId: string,
    subscriptionItemId?: string,
) => {

    const queryClient = useQueryClient();

    const subscriptionItemQuery = useQuery({
        queryKey: subscriptionItemsKeys.detail(subscriptionItemId ?? ''),
        queryFn: () => fetch(subscriptionItemId!),
        enabled: !!subscriptionItemId,
    });

    /** Detail mutations invalidate BOTH the list and the detail key (§8). */
    const invalidate = () => {
        queryClient.invalidateQueries({queryKey: subscriptionItemsKeys.lists()});
        if (subscriptionItemId) {
            queryClient.invalidateQueries({
                queryKey: subscriptionItemsKeys.detail(subscriptionItemId),
            });
        }
    };

    const createMutation = useMutation({
        // `subscription` is REQUIRED by CreateSubscriptionItemInput, so the
        // scope is supplied here rather than relying on the form to carry it.
        mutationFn: (values: SubscriptionItem) => add({
            ...values,
            subscription: values.subscription ?? subscriptionId,
        }),
        onSuccess: () => {
            notifySuccess('Subscription item created');
            invalidate();
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            if (!subscriptionItemId) {
                throw new Error('subscriptionItemId is required parameter');
            }
            return update({
                ...values,
                id: subscriptionItemId,
            });
        },
        onSuccess: () => {
            notifySuccess('Subscription item updated');
            invalidate();
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!subscriptionItemId) {
                throw new Error('subscriptionItemId is required parameter');
            }
            return deleteRecord(subscriptionItemId);
        },
        onSuccess: () => {
            notifySuccess('Subscription item deleted');
            invalidate();
        },
        onError: notifyError,
    });

    return {
        record: subscriptionItemQuery.data,
        loading: subscriptionItemQuery.isLoading,
        isError: subscriptionItemQuery.isError,
        fetchRecord: subscriptionItemQuery.refetch,
        // mutateAsync so the drawer can await the result before closing.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
